import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { UserCircle, Lock, Envelope, ShieldCheck, Warning, Palette, Eye, Skull, Crown } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { PasswordStrengthMeter } from '../components/PasswordStrengthMeter';
import { DangerZone } from '../components/DangerZone';
import { AvatarEditor, ProfileAvatar, ProfileStatus, StatusEditor } from '../components/ProfileIdentity';
import { Textarea } from '../components/ui/textarea';
import { USERNAME_COLORS, usernameColorClass } from '../lib/usernameColors';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProfilePage() {
  const { user, checkAuth } = useAuth();
  const [profileForm, setProfileForm] = useState({ name: '', username: '', bio: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [emailForm, setEmailForm] = useState({ new_email: '', password: '' });
  const [emailCodeStep, setEmailCodeStep] = useState(false);
  const [emailCode, setEmailCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const isElite = user?.membership_level > 0 || user?.role === 'admin';

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', username: user.username || '', bio: user.bio || '' });
      axios.get(`${API}/profile/stats`, { withCredentials: true })
        .then(({ data }) => {
          setStats(data);
          setProfileForm(form => ({ ...form, bio: data.bio || '' }));
        })
        .catch(() => setStats(null));
    }
  }, [user]);

  const applyUsernameColor = async (color) => {
    try {
      await axios.put(`${API}/profile/username-color`, { color }, { withCredentials: true });
      await checkAuth();
      setStats((s) => (s ? { ...s, username_color: color } : s));
      toast.success('Username colour updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not update colour');
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, profileForm, { withCredentials: true });
      await checkAuth();
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Update failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPass !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPass.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.newPass
      }, { withCredentials: true });
      toast.success('Password changed successfully!');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChangeRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-email-request`, emailForm, { withCredentials: true });
      toast.success(`Verification code sent to ${emailForm.new_email}`);
      setEmailCodeStep(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChangeVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-email-verify`, { code: emailCode }, { withCredentials: true });
      await checkAuth();
      toast.success('Email changed successfully!');
      setEmailCodeStep(false);
      setEmailCode('');
      setEmailForm({ new_email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSendEmailVerification = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/send-verification`, {}, { withCredentials: true });
      toast.success('Verification email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 sm:px-6" data-testid="profile-page">
      <div className="w-full max-w-4xl mx-auto">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-light mb-2" data-testid="profile-heading">
            Profile Dashboard
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            Manage your account settings and personal information
          </p>
        </div>

        <Card className="p-4 sm:p-6 mb-6 md:mb-8" data-testid="profile-overview">
          <div className="flex items-center space-x-4">
            <ProfileAvatar src={stats?.avatar_url} username={user.username} testId="profile-avatar" className="w-16 h-16 sm:w-20 sm:h-20" />
            <div className="flex-1 min-w-0">
              <h2 className={`text-xl sm:text-2xl font-light truncate ${usernameColorClass(user.username_color)}`} data-testid="profile-username">{user.username}</h2>
              <div className="flex flex-wrap items-center gap-x-2 text-xs sm:text-sm text-muted-foreground">
                <span className="truncate max-w-full" data-testid="profile-email">{user.email}</span>
                {user.email_verified ? (
                  <span className="flex items-center text-green-500 text-xs" data-testid="email-verified-badge">
                    <ShieldCheck size={14} weight="fill" className="mr-1" />
                    Verified
                  </span>
                ) : (
                  <button
                    onClick={handleSendEmailVerification}
                    className="flex items-center text-yellow-500 text-xs hover:underline"
                    data-testid="email-unverified-badge"
                  >
                    <Warning size={14} weight="fill" className="mr-1" />
                    Not verified - Send code
                  </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Membership: <span className="text-primary">{user.membership_level === 0 ? 'Free' : `Level ${user.membership_level}`}</span>
              </p>
              {stats && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5" data-testid="profile-views-count">
                    <Eye size={14} className="text-amber-300" />
                    <span className="text-white">{stats.profile_views}</span> profile visits
                  </span>
                  <span data-testid="profile-unique-visitors">
                    <span className="text-white">{stats.unique_visitors}</span> unique visitors
                  </span>
                  <Link to={`/u/${user.username}`} className="text-amber-300 hover:underline" data-testid="view-public-profile-link">
                    View public profile
                  </Link>
                </div>
              )}
            </div>
          </div>
          {stats?.bio && <p className="mt-5 text-sm text-muted-foreground whitespace-pre-wrap break-words" data-testid="profile-bio-display">{stats.bio}</p>}
          <ProfileStatus status={stats?.status} expiresAt={stats?.status_expires_at} testId="profile-live-status" />
        </Card>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="details" className="text-xs sm:text-sm" data-testid="tab-details">
              <UserCircle size={14} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Personal&nbsp;</span>Details
            </TabsTrigger>
            <TabsTrigger value="password" className="text-xs sm:text-sm" data-testid="tab-password">
              <Lock size={14} className="mr-1 sm:mr-2" />
              Password
            </TabsTrigger>
            <TabsTrigger value="email" className="text-xs sm:text-sm" data-testid="tab-email">
              <Envelope size={14} className="mr-1 sm:mr-2" />
              Email
            </TabsTrigger>
            <TabsTrigger value="appearance" className="text-xs sm:text-sm" data-testid="tab-appearance">
              <Palette size={14} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Appearance</span><span className="sm:hidden">Style</span>
            </TabsTrigger>
            <TabsTrigger value="danger" className="text-xs sm:text-sm text-rose-300" data-testid="tab-danger">
              <Skull size={14} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Account</span><span className="sm:hidden">Danger</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details">
            <Card className="p-6" data-testid="details-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Personal Details</h3>
              <AvatarEditor profile={stats} username={user.username} onChange={changes => setStats(previous => ({ ...previous, ...changes }))} />
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    required
                    data-testid="profile-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    value={profileForm.username}
                    onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                    required
                    data-testid="profile-username-input"
                  />
                </div>
                <div>
                  <Label htmlFor="profile-bio">Bio</Label>
                  <Textarea id="profile-bio" value={profileForm.bio} onChange={event => setProfileForm({ ...profileForm, bio: event.target.value })} maxLength={500} rows={4} placeholder="Share a little about yourself. Keep personal details private." className="mt-2 resize-y" data-testid="profile-bio-input" />
                  <p className="text-xs text-muted-foreground mt-2" data-testid="profile-bio-help">{profileForm.bio.length}/500 · Shown on your public profile</p>
                </div>
                {user.date_of_birth && (
                  <div>
                    <Label>Date of Birth</Label>
                    <Input value={user.date_of_birth} disabled data-testid="profile-dob-display" />
                    <p className="text-xs text-muted-foreground mt-1">Cannot be changed</p>
                  </div>
                )}
                <Button
                  type="submit"
                  className="rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="profile-save-button"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
              <StatusEditor profile={stats} onChange={changes => setStats(previous => ({ ...previous, ...changes }))} />
            </Card>
          </TabsContent>

          <TabsContent value="password">
            <Card className="p-6" data-testid="password-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Change Password</h3>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <Label htmlFor="current">Current Password</Label>
                  <Input
                    id="current"
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                    required
                    data-testid="password-current-input"
                  />
                </div>
                <div>
                  <Label htmlFor="newPass">New Password</Label>
                  <Input
                    id="newPass"
                    type="password"
                    value={passwordForm.newPass}
                    onChange={(e) => setPasswordForm({ ...passwordForm, newPass: e.target.value })}
                    required
                    data-testid="password-new-input"
                  />
                  <PasswordStrengthMeter password={passwordForm.newPass} />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm New Password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                    required
                    data-testid="password-confirm-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="rounded-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="password-change-button"
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="email">
            <Card className="p-6" data-testid="email-section">
              <h3 className="text-2xl tracking-tight font-light mb-4">Change Email Address</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Current email: <span className="text-white">{user.email}</span>
              </p>

              {!emailCodeStep ? (
                <form onSubmit={handleEmailChangeRequest} className="space-y-4">
                  <div>
                    <Label htmlFor="new_email">New Email</Label>
                    <Input
                      id="new_email"
                      type="email"
                      value={emailForm.new_email}
                      onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })}
                      required
                      data-testid="email-new-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_password">Confirm with your current password</Label>
                    <Input
                      id="email_password"
                      type="password"
                      value={emailForm.password}
                      onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                      required
                      data-testid="email-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="rounded-full bg-primary hover:bg-primary/90"
                    disabled={loading}
                    data-testid="email-request-button"
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleEmailChangeVerify} className="space-y-4">
                  <div className="p-4 bg-primary/10 border border-primary/30">
                    <p className="text-sm">
                      Verification code sent to <span className="text-primary">{emailForm.new_email}</span>
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email_code">Verification Code</Label>
                    <Input
                      id="email_code"
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      placeholder="000000"
                      value={emailCode}
                      onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ''))}
                      required
                      className="text-center text-2xl tracking-[0.5em]"
                      data-testid="email-code-input"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      type="submit"
                      className="rounded-full bg-primary hover:bg-primary/90"
                      disabled={loading || emailCode.length !== 6}
                      data-testid="email-verify-button"
                    >
                      {loading ? 'Verifying...' : 'Verify & Change Email'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="rounded-full"
                      onClick={() => { setEmailCodeStep(false); setEmailCode(''); }}
                      data-testid="email-cancel-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </Card>
          </TabsContent>
          <TabsContent value="appearance">
            <Card className="p-6" data-testid="appearance-section">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h3 className="text-2xl tracking-tight font-light">Username Colour</h3>
                {!isElite && (
                  <Link to="/membership" data-testid="appearance-upgrade-link">
                    <Button size="sm" className="rounded-full bg-amber-500 hover:bg-amber-500/90 text-black">
                      <Crown size={14} className="mr-1.5" weight="fill" /> Unlock with Elite
                    </Button>
                  </Link>
                )}
              </div>
              <p className="text-xs text-muted-foreground mb-6">
                {isElite
                  ? 'Pick a colour or a mixed gradient for your username across the site.'
                  : 'Colour and rainbow username styles are an Elite member perk. Upgrade to make your name stand out.'}
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {USERNAME_COLORS.map((option) => {
                  const locked = option.elite && !isElite;
                  const selected = (user.username_color || 'default') === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      disabled={locked}
                      onClick={() => applyUsernameColor(option.id)}
                      className={`p-3 rounded-lg border text-center transition-colors ${
                        selected ? 'border-amber-400 bg-amber-400/10' : 'border-white/10 bg-white/5 hover:border-white/30'
                      } ${locked ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid={`username-color-${option.id}`}
                    >
                      <span className={`block text-sm truncate ${option.className}`}>{user.username}</span>
                      <span className="block text-[11px] text-muted-foreground mt-1">
                        {option.label}{locked ? ' · Elite' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="danger">
            <DangerZone />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
